import {
  useState, useMemo, useRef, useEffect,
} from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import useLiterals from 'utils/hooks/useLiterals';
import Layout from 'components/Layout';
import AppShell from 'components/Layout/AppShell';
import PaymentBox from 'components/PaymentBox';
import StepsProgress from 'components/UI/StepsProgress';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import { ReactComponent as CardIcon } from 'assets/icons/card.svg';
import { ReactComponent as BillingIcon } from 'assets/icons/money_red.svg';
import { formatCurrency } from 'utils/formatCurrency';
import PaymentReference from 'components/PaymentReference';
import BBVA from 'components/BBVA';
import MercadoPago from 'components/MercadoPago';
import { useMercadoPagoVisible } from 'modules/mercadopago/useMercadoPagoVisible';
import PaymentOutOfHour from 'components/PaymentOutOfHour';
import useOutOfOffice from 'utils/hooks/useOutOfOffice';
import {
  CONTRACT,
  CONTRACT_STATUS,
  PAYCASH_MAX_AMOUNT,
  PROVIDER_TYPE,
  SECTION,
  SECTION_MIN_AMOUNT,
  SECTION_PAYMENT_OPTIONS_KEYS,
  PAYMENT_OPTIONS_KEYS,
} from 'constants/global';
import { ROUTE_PATH } from 'routes';
import { contractsActions } from 'modules/contracts';
import { sectionsActions } from 'modules/sections';
import { useContractOrSection } from 'utils/hooks/useContractOrSection';
import { formatDate } from 'utils/formatDate';
import InputAmountSteps from 'components/UI/InputAmountSteps';
import Translate from 'components/Translate';
import Modal from 'components/Modal';
import InlineError from 'components/UI/InlineError';
import { translate } from 'utils/translate';
import { formatContractPaymentOptions } from 'utils/formatContratPaymentOption';
import isPaymentCardBlocked from 'utils/isPaymentCardBlocked';
import { getContractEmpaqueFieldsFromResponse } from 'utils/paymentSummaryEmpaque';
import PaymentResumeEmpaqueRow from './components/PaymentResumeEmpaqueRow';
import ContractPaymentOptions from './components/ContractPaymentOptions';
import PaymentTypeAlertModal from './components/PaymentTypeAlertModal';
import './styles.scss';

const ContractPay = () => {
  const literals = useLiterals('contractPay');
  const contractLiterals = useLiterals('contract');
  const dispatch = useDispatch();
  const { schedule: paymentsSchedule, isOutOfHour } = useOutOfOffice();
  const params = useParams();
  const navigate = useNavigate();
  const { type, contract, section } = useContractOrSection(ROUTE_PATH.CONTRACT_PAYMENT, params.id);
  const { visible: showMercadoPago } = useMercadoPagoVisible();

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [coverageId, setCoverageId] = useState(null);
  const [amount, setAmount] = useState({ capital: 0, coverage: 0 });
  const [amountError, setAmountError] = useState({ capital: false, coverage: false, extra: false });
  const [extraAmount, setExtraAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showExtensionNotice, setShowExtensionNotice] = useState(false);
  const [showLiquidationAlert, setShowLiquidationAlert] = useState(false);
  const [step, setStep] = useState(0);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const cardsContainerRef = useRef(null);
  const paymentEntity = contract || section;
  const cardPaymentBlocked = isPaymentCardBlocked(paymentEntity);
  const paycashAvailable = (amount.capital || 0)
    + (amount.coverage || 0)
    + (extraAmount || 0) <= PAYCASH_MAX_AMOUNT;

  useEffect(() => {
    if (!cardPaymentBlocked) return;
    if ([PROVIDER_TYPE.BANCOMER, PROVIDER_TYPE.MERCADO_PAGO].includes(paymentMethod)) {
      setPaymentMethod(null);
    }
  }, [cardPaymentBlocked, paymentMethod]);
  const isExtensionPayment = (payment) => {
    if (!payment || type !== CONTRACT) return false;
    return payment.paymentKey === String(PAYMENT_OPTIONS_KEYS.EXTENSION)
      || payment.paymentKey === String(PAYMENT_OPTIONS_KEYS.EXTENSION_EXT);
  };

  const payments = useMemo(() => {
    let options = [];
    let coverage = null;
    if (type === CONTRACT && contract) {
      options = formatContractPaymentOptions(contract?.OpcionesPago, literals);
    }
    if (type === SECTION && section) {
      if (section?.extension) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.EXTENSION,
          paymentKey: 'extensionPayment',
          title: literals.extensionPayment,
          amount: section.extensionPayment || 0,
          min: null,
          max: null,
          limitDate: section.expirationDate,
          lastTerm: section.currentTerm === section.maximumTerm,
        });
      }
      if (section?.minimum) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.MINIMUM,
          paymentKey: 'minimumPayment',
          title: literals.minimumPayment,
          amount: section.minimumPayment || 0,
          min: section.minimumPaymentWithDepositMin || null,
          max: section.minimumPaymentWithDepositMax || null,
          limitDate: section.expirationDate,
        });
      }
      // if (section?.suggested) {
      //   options.push({
      //     key: SECTION_PAYMENT_OPTIONS_KEYS.SUGGESTED,
      //     paymentKey: 'suggestedPayment',
      //     title: literals.suggestedPayment,
      //     amount: section.suggestedPayment || 0,
      //     min: section.suggestedPaymentWithDepositMin || null,
      //     max: section.suggestedPaymentWithDepositMax || null,
      //     limitDate: section.expirationDate,
      //   });
      // }
      if (section.remainingCapital >= 120 && section.currentTerm < section.maximumTerm) {
        options.push({
          key: SECTION_PAYMENT_OPTIONS_KEYS.ABONO_20,
          paymentKey: 'abono20OrMore',
          title: literals.abono20OrMore,
          amount: 20,
          min: 20,
          max: section.remainingCapital - 100,
          limitDate: section.expirationDate,
        });
      }
      if (section.coverageBalance > 0 && section.coverageId) {
        const minPayment = Math.ceil(section.coverageAmount * 0.1);
        coverage = {
          id: section.coverageId,
          min: section.minimumCoverage || minPayment,
          max: section.coverageBalance || 0,
          balance: section.coverageBalance || 0,
          limit: Math.floor((section.coverageAmount || 0) * 0.9 - (section.coveragePaid || 0)),
        };
        coverage.unavailable = coverage.min > coverage.limit;
      }
    }
    return {
      options,
      coverage,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, section, type]);

  const handleClickStep0 = () => {
    if (!paycashAvailable && paymentMethod === PROVIDER_TYPE.PAYCASH) {
      setPaymentMethod(null);
    }
    setStep(1);
  };

  const handleSelectPayment = (payment) => {
    const selected = payments.options.find((option) => option.key === payment);
    setSelectedPayment(selected);
    setAmount((prev) => ({
      ...prev,
      capital: selected?.amount || 0,
      coverage: prev.coverage || payments?.coverage?.min || 0,
    }));
    setExtraAmount(0);
    setAmountError((prev) => ({ ...prev, capital: false, extra: false }));
    if (type === SECTION) {
      setShowExtensionNotice(selected?.key === 'extension' && selected?.lastTerm);
    }
    // Mostrar alerta si el tipo de pago es LIQUIDACION
    const isLiquidation = selected?.paymentKey === PAYMENT_OPTIONS_KEYS.LIQUIDACION
      || selected?.paymentKey === PAYMENT_OPTIONS_KEYS.LIQUIDACION_A
      || (selected?.title && selected.title.toLowerCase().includes('liquidaci'));
    if (isLiquidation) {
      setShowLiquidationAlert(true);
    }
  };

  const isSectionAdjustableAmount = type === SECTION
    && selectedPayment
    && (
      selectedPayment.key === SECTION_PAYMENT_OPTIONS_KEYS.MINIMUM
      || selectedPayment.key === SECTION_PAYMENT_OPTIONS_KEYS.ABONO_20
    );

  const handleSetAmount = (newAmount, key = 'capital') => {
    if (key === 'coverage') {
      const newAmountError = newAmount < payments.coverage.min || newAmount > payments.coverage?.limit;
      setAmount((prev) => ({ ...prev, coverage: newAmount }));
      setAmountError((prev) => ({ ...prev, coverage: newAmountError }));
      return;
    }
    const newAmountError = (isSectionAdjustableAmount && (newAmount === null || newAmount === ''))
      || newAmount < selectedPayment?.amount
      || (selectedPayment?.min
        && newAmount < selectedPayment.min
        && newAmount !== selectedPayment?.amount)
      || (selectedPayment?.max && newAmount > selectedPayment.max);

    setAmount((prev) => ({ ...prev, capital: newAmount }));
    setAmountError((prev) => ({ ...prev, capital: newAmountError }));
  };

  const handleSetExtraAmount = (newAmount) => {
    if (!selectedPayment) return;
    const maxAbono = selectedPayment?.max
      ? selectedPayment.max - (selectedPayment.amount || 0)
      : 0;
    const newAmountError = !!maxAbono
      && (newAmount < SECTION_MIN_AMOUNT || newAmount > maxAbono);
    setExtraAmount(newAmount);
    setAmountError((prev) => ({ ...prev, extra: newAmountError }));
  };

  const renderPaymentInfo = () => {
    const extensionSelected = isExtensionPayment(selectedPayment);
    const extensionAbonoMax = extensionSelected && selectedPayment?.max
      ? selectedPayment.max - (selectedPayment.amount || 0)
      : 0;
    return (
      <div>
        {payments.options.length > 0 ? (
          <ContractPaymentOptions
            options={payments.options}
            literals={literals}
            coverage={payments.coverage}
            selectedPayment={selectedPayment?.key || ''}
            selectedCoverage={coverageId}
            onSelectPayment={handleSelectPayment}
            onChangeCoverage={setCoverageId}
          />
        ) : (
          <InlineError className='mt-6 mb-6' error={translate(contractLiterals.noPaymentOptions)} />
        )}
        {selectedPayment && (
          <>
            <p className='mt-2 mb-2 text-dark-gray font-bold'>{literals.amount}</p>
            {!extensionSelected && (
              <InputAmountSteps
                value={amount.capital}
                originalValue={selectedPayment.amount}
                min={selectedPayment.min}
                max={selectedPayment.max}
                error={amountError.capital}
                clampOnBlur={!isSectionAdjustableAmount}
                onChange={(v) => handleSetAmount(v, 'capital')}
              />
            )}
            {extensionSelected && (
              <p className='mt-1 mb-2'>
                <b>{formatCurrency(selectedPayment.amount)}</b>
              </p>
            )}
            {extensionSelected && (
              <>
                <p className='mt-4 mb-2 font-bold'>{literals.abono20OrMore}</p>
                <InputAmountSteps
                  value={extraAmount}
                  originalValue={0}
                  min={SECTION_MIN_AMOUNT}
                  max={extensionAbonoMax}
                  error={amountError.extra}
                  onChange={(v) => handleSetExtraAmount(v)}
                />
              </>
            )}
            {coverageId ? (
              <>
                <p className='mt-4 mb-2 font-bold'>{literals.coverageAmount}</p>
                <InputAmountSteps
                  value={amount.coverage}
                  min={payments.coverage.min}
                  max={payments.coverage.limit}
                  error={amountError.coverage}
                  decimals={false}
                  onChange={(v) => handleSetAmount(v, 'coverage')}
                />
                {amount.coverage > payments.coverage.limit
                  && amount.coverage <= payments.coverage.max && (
                  <Translate
                    className='mt-2 mb-0 text-danger'
                    literal={literals.maxAmountCoverage}
                    vars={{ amount: formatCurrency(payments.coverage.limit) }}
                  />
                )}
                {!amountError.coverage && !amountError.capital && !amountError.extra && (
                  <Translate
                    className='mt-6 mb-0 font-bold'
                    literal={literals.totalAmountToPay}
                    vars={{
                      amount: formatCurrency(
                        amount.capital + amount.coverage + (extraAmount || 0),
                      ),
                    }}
                  />
                )}
              </>
            ) : null}
            <Button
              onClick={handleClickStep0}
              className='mt-6'
              disabled={
                !selectedPayment
                || amountError.capital
                || amountError.coverage
                || amountError.extra
              }
              text={literals.common.next}
            />
          </>
        )}
      </div>
    );
  };

  const renderOutOfHour = () => {
    return (
      <>
        <PaymentBox
          type={type}
          data={contract || section || null}
          showAction={false}
          inBox={false}
        />
        <PaymentOutOfHour schedule={paymentsSchedule} />
      </>
    );
  };

  const renderStep0 = () => {
    return (
      <>
        <PaymentBox
          type={type}
          data={contract || section || null}
          showAction={false}
          inBox={false}
        />
        {renderPaymentInfo()}
      </>
    );
  };

  const renderStep1 = () => {
    const bancomerDisabled = cardPaymentBlocked;
    const mercadoPagoDisabled = cardPaymentBlocked;
    return (
      <>
        <h3 className='mb-4'>{literals.paymentMethod}</h3>
        <div className='grid-container' ref={cardsContainerRef}>
          {!bancomerDisabled && (
            <Box
              className={`${
                paymentMethod === PROVIDER_TYPE.BANCOMER && 'selected'
              } payment-option-box flex items-start text-center`}
              onClick={() => setPaymentMethod(PROVIDER_TYPE.BANCOMER)}
            >
              <div className='py-2'>
                <CardIcon className='' width={35} />
                <p className='mt-4 mb-0 text-xs'>{literals.cardBancomerDescription}</p>
                <p className='mt-2 mb-0 text-sm font-semibold'>{literals.card}</p>
              </div>
            </Box>
          )}
          {showMercadoPago && !mercadoPagoDisabled && (
            <Box
              className={`${
                paymentMethod === PROVIDER_TYPE.MERCADO_PAGO && 'selected'
              } payment-option-box flex items-start text-center`}
              onClick={() => !mercadoPagoDisabled && setPaymentMethod(PROVIDER_TYPE.MERCADO_PAGO)}
            >
              <div className='py-2'>
                <CardIcon className='' width={35} />
                <p className='mt-4 mb-0 text-xs'>{literals.mercadopagoDescription}</p>
                <p className='mt-2 mb-0 text-sm font-semibold'>{literals.card}</p>
              </div>
            </Box>
          )}
          <Box
            className={`${
              paymentMethod === PROVIDER_TYPE.PAYCASH && 'selected'
            } payment-option-box flex items-start text-center ${
              paycashAvailable ? '' : 'disabled'
            }`}
            onClick={() => paycashAvailable && setPaymentMethod(PROVIDER_TYPE.PAYCASH)}
          >
            <div className='py-2'>
              <BillingIcon className='' width={35} />
              <p className='mt-4 mb-0 text-xs'>{literals.referencePayment}</p>
              <p className='mt-2 mb-0 text-sm font-semibold'>{literals.paycash}</p>
              {!paycashAvailable && (
                <Translate
                  className='mb-0 text-xs'
                  literal={literals.maxAmountPaycash}
                  vars={{ amount: formatCurrency(PAYCASH_MAX_AMOUNT) }}
                />
              )}
            </div>
          </Box>
          <Box
            className={`${
              paymentMethod === PROVIDER_TYPE.PUNTORED && 'selected'
            } payment-option-box flex items-start text-center`}
            onClick={() => setPaymentMethod(PROVIDER_TYPE.PUNTORED)}
          >
            <div className='py-2'>
              <BillingIcon className='' width={35} />
              <p className='mt-4 mb-0 text-xs'>{literals.referencePayment}</p>
              <p className='mt-2 mb-0 text-sm font-semibold'>{literals.puntored}</p>
            </div>
          </Box>
        </div>
        <Button
          onClick={() => setStep(2)}
          className='mt-6'
          type='primary'
          disabled={!paymentMethod}
          text={literals.pay}
        />
      </>
    );
  };

  const renderStep2 = () => {
    const capitalWithAbono = amount.capital + (extraAmount || 0);
    if (paymentMethod === PROVIDER_TYPE.PAYCASH || paymentMethod === PROVIDER_TYPE.PUNTORED) {
      return (
        <PaymentReference
          type={type}
          provider={paymentMethod}
          id={params.id}
          payment={selectedPayment}
          amount={capitalWithAbono}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.BANCOMER) {
      return (
        <BBVA
          type={type}
          id={params.id}
          payment={selectedPayment}
          amount={capitalWithAbono}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
          onClose={() => {
            setStep(1);
          }}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.MERCADO_PAGO) {
      const totalAmount = capitalWithAbono + (coverageId ? amount.coverage : 0);
      return (
        <MercadoPago
          amount={capitalWithAbono}
          type={type}
          id={params.id}
          payment={selectedPayment}
          coverage={coverageId ? amount.coverage : 0}
          coverageId={coverageId}
          onClose={() => {
            setPaymentMethod(null);
            const path = type === CONTRACT
              ? ROUTE_PATH.setContractPayment(params.id)
              : ROUTE_PATH.setSectionPayment(params.id);
            navigate(path, { replace: true });
          }}
          onSuccess={(response) => {
            if (type === CONTRACT) {
              dispatch(contractsActions.needUpdate());
            } else {
              dispatch(sectionsActions.needUpdate());
            }
            setPaymentSummary({
              id: params.id,
              type,
              paymentName: selectedPayment?.title || selectedPayment?.paymentName,
              paymentKey: selectedPayment?.paymentKey,
              ...getContractEmpaqueFieldsFromResponse(response, params.id),
              amount: amount.capital + (extraAmount || 0),
              coverage: coverageId ? amount.coverage : 0,
              totalAmount,
              date: new Date(),
              status: CONTRACT_STATUS.COMPLETED,
            });
          }}
          onPending={(pendingResult) => {
            if (type === CONTRACT) {
              dispatch(contractsActions.needUpdate());
            } else {
              dispatch(sectionsActions.needUpdate());
            }
            setPaymentSummary({
              id: params.id,
              type,
              paymentName: selectedPayment?.title || selectedPayment?.paymentName,
              paymentKey: selectedPayment?.paymentKey,
              ...getContractEmpaqueFieldsFromResponse(pendingResult, params.id),
              amount: amount.capital + (extraAmount || 0),
              coverage: coverageId ? amount.coverage : 0,
              totalAmount,
              date: new Date(),
              status: CONTRACT_STATUS.PENDING,
              warningMessage: pendingResult?.message,
              mpStatus: pendingResult?.status,
              mpStatusDetail: pendingResult?.statusDetail,
              mpPaymentId: pendingResult?.paymentId,
            });
          }}
        />
      );
    }
    return <h1>Próximamente</h1>;
  };

  const handleBack = () => {
    if (paymentSummary) {
      setPaymentSummary(null);
      setStep(0);
      setPaymentMethod(null);
      navigate(ROUTE_PATH.PAYMENTS);
      return;
    }
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    navigate(-1);
  };

  const renderPaymentSummary = () => {
    if (!paymentSummary) return null;
    const summary = paymentSummary;
    return (
      <div className='payment-resume'>
        <h1 className='mb-6 text-primary'>{literals.paymetResume}</h1>
        {summary.warningMessage && (
          <div className='alert alert-warning mb-4' role='alert'>
            <strong>{literals.warningTitle || 'Atención'}</strong>
            {' '}
            {summary.warningMessage}
          </div>
        )}
        <div className='payment-resume-contract-info mb-2'>
          <div className='flex justify-between'>
            <h2 className='font-bold'>{literals.common[summary.type.toLowerCase()]}</h2>
            <h3 className='text-neutral-500'>{summary.id}</h3>
          </div>
          <PaymentResumeEmpaqueRow payment={summary} label={literals.empaque} />
        </div>
        {summary.paymentName ? (
          <div className='flex justify-between mb-2'>
            <h2 className='font-bold'>{literals.paymentConcept}</h2>
            <h3 className='text-neutral-500 text-right'>{summary.paymentName}</h3>
          </div>
        ) : null}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.date}</h2>
          <h3 className='text-neutral-500'>{formatDate(summary.date, 'DD/MM/YYYY')}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.paymentMethod2}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.mercadopago}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.status}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.statusValues[summary.status] || summary.status}</h3>
        </div>
        <div className='separator my-4' />
        {summary.coverage > 0 && (
          <>
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{summary.type === CONTRACT ? literals.contractAmount : literals.sectionAmount}</h2>
              <h3 className='text-primary font-bold'>{formatCurrency(summary.amount)}</h3>
            </div>
            <div className='flex justify-between mb-2'>
              <h2 className='font-bold'>{literals.coverageAmount}</h2>
              <h3 className='text-primary font-bold'>{formatCurrency(summary.coverage)}</h3>
            </div>
          </>
        )}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.totalAmount}</h2>
          <h3 className='text-primary font-bold'>{formatCurrency(summary.totalAmount)}</h3>
        </div>
        <Button
          className='mt-6'
          type='primary'
          text={literals.common.back}
          onClick={() => {
            setPaymentSummary(null);
            setStep(0);
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS);
          }}
        />
      </div>
    );
  };

  return (
    <Layout title={literals.title} bottomMenu onBack={handleBack}>
      <AppShell>
        <div className='contract-payment__container'>
          <Box padding='sm' className='mb-4'>
            {isOutOfHour && renderOutOfHour()}
            {!isOutOfHour && paymentSummary && renderPaymentSummary()}
            {!isOutOfHour && !paymentSummary && (
              <>
                <h1 className='text-primary my-2'>
                  {
                  // eslint-disable-next-line no-nested-ternary
                    step === 0
                      ? literals.amortize
                      : step === 1
                        ? literals.paymentMethod2
                        : literals.pay
                  }
                </h1>
                <StepsProgress
                  className='mb-4 w-full'
                  steps={[
                    { label: literals.amortize, value: 0 },
                    { label: literals.paymentMethod2, value: 1 },
                    { label: literals.pay, value: 2 },
                  ]}
                  current={step}
                />
                {step === 0 && renderStep0()}
                {step === 1 && renderStep1()}
                {step === 2 && renderStep2()}
              </>
            )}
          </Box>
        </div>
        {showExtensionNotice && (
          <Modal
            onClose={() => setShowExtensionNotice(false)}
            showCloseButton={false}
            closeOnBackgroundClick={false}
          >
            <Translate className='mt-2 mb-4' literal={literals.sectionExtensionNotice} />
            <p className='text-right'>
              <b>{`${literals.pendingAmount}: ${formatCurrency(section?.remainingCapital)}`}</b>
            </p>
            <Button text={literals.common.close} onClick={() => setShowExtensionNotice(false)} />
          </Modal>
        )}
        <PaymentTypeAlertModal
          show={showLiquidationAlert}
          title={literals.liquidationAlertTitle}
          message={literals.liquidationAlertMessage}
          buttonText={literals.accept}
          onClose={() => setShowLiquidationAlert(false)}
        />
      </AppShell>
    </Layout>
  );
};

export default ContractPay;
